import { toHHmm, toMinutes, TYPE_COLOR } from "./schedulerUtils";
import type { ContentType, Program, ProgramColor } from "./types";

/** The display fields the backend snapshots per block (ChannelPrograms.Metadata). */
export interface ProgramMetadata {
  type?: ContentType;
  color?: ProgramColor;
  thumbnail?: string;
  logos?: string[];
  channelLogo?: string;
  /** Legacy home of the original runtime: rows written before the
   * DurationSec column existed carry it here. Read fallback only; new
   * saves send the top-level durationSec instead. */
  durationSec?: number;
  durationEstimated?: boolean;
  contentDate?: string;
  /** Full source record from the content feed (the whole Game object). */
  source?: Record<string, unknown>;
}

/** One admin row exactly as serializeAdminProgram emits it. */
export interface AdminProgramRow {
  id: number;
  channelId: number;
  scheduleDate: string;
  startMin: number;
  endMin: number;
  contentType: string;
  contentId: string;
  videoUrl: string;
  trimInSec: number;
  trimOutSec: number;
  /** The clip's original pre-trim runtime (ChannelPrograms.DurationSec);
   * null on rows whose runtime was never captured. */
  durationSec: number | null;
  title: string;
  description: string | null;
  metadata: ProgramMetadata | null;
}

/** One block of the PUT /admin/channel-schedule/{channelId}/{date} payload. */
/** The schedulable content kinds and the library id prefix each uses, so
 * a program's contentId says what it is: "vod-<gameId>", "up-<assetId>",
 * "wz-<whizzardId>". */
export const CONTENT_PREFIX = { game: "vod", upload: "up", whizzard: "wz" } as const;
export type ApiContentType = keyof typeof CONTENT_PREFIX;

function contentTypeOf(contentId: string): ApiContentType {
  const entry = (Object.entries(CONTENT_PREFIX) as [ApiContentType, string][]).find(([, prefix]) =>
    contentId.startsWith(`${prefix}-`),
  );
  return entry ? entry[0] : "game";
}

export interface ApiBlock {
  contentType: ApiContentType;
  contentId: string;
  startMin: number;
  endMin: number;
  trimInSec: number;
  trimOutSec: number;
  durationSec: number | null;
  title: string;
  description: string | null;
  metadata: Required<Omit<ProgramMetadata, "source" | "durationSec">> &
    Pick<ProgramMetadata, "source">;
}

/**
 * Wire row -> scheduler Program. Ids become the strings the UI keys by, the
 * library's `vod-` prefix is restored onto contentId so replace/compare
 * against ContentItems keeps working, and minutes become the "HH:mm" the
 * grid renders (1440 = "24:00"; a cross-midnight end like 1530 = "25:30").
 * Metadata may be missing on rows written before a field existed, so every
 * display field has a fallback.
 */
export function apiToProgram(row: AdminProgramRow): Program {
  const meta = row.metadata ?? {};
  return {
    id: String(row.id),
    channelId: String(row.channelId),
    // The library prefix is restored from the row's type (CONTENT_PREFIX).
    contentId: `${CONTENT_PREFIX[row.contentType as ApiContentType] ?? "vod"}-${row.contentId}`,
    title: row.title,
    description: row.description,
    type: meta.type ?? "game",
    color: meta.color ?? TYPE_COLOR[meta.type ?? "game"],
    thumbnail: meta.thumbnail ?? "",
    logos: meta.logos,
    channelLogo: meta.channelLogo,
    videoUrl: row.videoUrl || undefined,
    durationSec: row.durationSec ?? meta.durationSec ?? row.trimOutSec,
    durationEstimated: meta.durationEstimated,
    contentDate: meta.contentDate ?? "",
    source: meta.source,
    date: row.scheduleDate,
    startTime: toHHmm(row.startMin),
    endTime: toHHmm(row.endMin),
    trimInSec: row.trimInSec,
    trimOutSec: row.trimOutSec,
  };
}

/** Scheduler Program -> save payload block. The library prefix decides the
 * content type and is stripped back off, so the backend gets the raw game
 * id (resolved to its vod_url) or upload asset id (resolved to its ready
 * manifest). */
export function programToApiBlock(program: Program): ApiBlock {
  const contentType = contentTypeOf(program.contentId);
  return {
    contentType,
    contentId: program.contentId.startsWith(`${CONTENT_PREFIX[contentType]}-`)
      ? program.contentId.slice(CONTENT_PREFIX[contentType].length + 1)
      : program.contentId,
    startMin: toMinutes(program.startTime),
    endMin: toMinutes(program.endTime),
    trimInSec: program.trimInSec,
    trimOutSec: program.trimOutSec,
    durationSec: program.durationSec ?? null,
    title: program.title,
    description: program.description,
    metadata: {
      type: program.type,
      color: program.color,
      thumbnail: program.thumbnail,
      logos: program.logos ?? [],
      channelLogo: program.channelLogo ?? "",
      durationEstimated: program.durationEstimated ?? false,
      contentDate: program.contentDate,
      ...(program.source ? { source: program.source } : {}),
    },
  };
}
